import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table } from '@/lib/components/Table';
import { EmptyScreenTable } from '@/lib/v2/examples';
import { EmptyCampaignsIcon, EmptyCampaignsNoResultIcon } from '@/lib/v2/icons/general';

import { useCampaignsStateContext } from '@/src/modules/CampaignsModule/contexts/CampaignsContext';

import { useCampaignTableColumns } from './hooks/useCampaignTableColumns';
import { useHandleEvents } from './hooks/useHandleEvents';

import {
  atomCampaignList,
  atomLoadingProcess,
  atomSelectedRows,
  atomTotalsProcess,
} from '@/modules/CampaignsModule/atoms/campaignAtom';
import { EmptyTableWithoutData } from '@/modules/CampaignsModule/components/Campaigns/TableContainer/components/EmptyTableWithoutData';

const CampaignsTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { campaignsColumns, changeTablePage } = useCampaignTableColumns();
  const { handleClickEmptyScreen, handleClickEmptyScreenNoResult } = useHandleEvents();
  const { totalPages, gotoPageIndex } = useCampaignsStateContext();

  const setSelectedRows = useSetAtom(atomSelectedRows);
  const totalsCampaignList = useAtomValue(atomTotalsProcess);
  const loadingProcess = useAtomValue(atomLoadingProcess);
  const campaignList = useAtomValue(atomCampaignList);

  return (
    <div className="w-full text-base text-[#364365] 2xl:text-14">
      <Table
        noNegativeMargin
        classNamePagination="px-4 mb-2"
        columns={campaignsColumns}
        data={campaignList}
        emptyScreen={{
          totalCount: totalsCampaignList.total,
          noResult: (
            <EmptyScreenTable
              actionText={t(
                'CAMPAIGNS_MAIN.EMPTY_SCREEN_TABLE_NO_RESULTS.emptyCampaignsActionText'
              )}
              description={t(
                'CAMPAIGNS_MAIN.EMPTY_SCREEN_TABLE_NO_RESULTS.emptyCampaignsDescription'
              )}
              image={<EmptyCampaignsNoResultIcon />}
              title={t('CAMPAIGNS_MAIN.EMPTY_SCREEN_TABLE_NO_RESULTS.emptyCampaignsTitle')}
              onClickAction={handleClickEmptyScreenNoResult}
            />
          ),
          noData: (
            <EmptyScreenTable
              descriptionNode={<EmptyTableWithoutData onClickAction={handleClickEmptyScreen} />}
              image={<EmptyCampaignsIcon />}
              title={t('CAMPAIGNS_MAIN.EMPTY_SCREEN_TABLE.emptyCampaignsTitle')}
            />
          ),
        }}
        gotoPageIndex={gotoPageIndex}
        isLoading={loadingProcess}
        setSelectedRows={setSelectedRows}
        withCheckbox={true}
        withPagination={{
          totalPages: totalPages,
          fetchData: changeTablePage,
        }}
        onRowClick={(value) => {
          navigate(`${value?.campaign?.campaignId}`);
        }}
      />
    </div>
  );
};

export default CampaignsTable;
